import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import { CustomerWorldMap } from "@crm-src/components/customer-world-map"
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"
import { customersApi } from "@crm-src/lib/customers-api"
import { productsApi } from "@crm-src/lib/products-api"
import type { Customer } from "@crm-src/types/customer"

// Filter interfaces  
interface ProductHierarchy {
  products: Array<{
    id: number
    name: string
    code?: string
  }>
  product_features: Record<number, Array<{
    id: number
    name: string
    code?: string
  }>>
}

interface WorldMapFilters {
  customerType: 'all' | 'leads' | 'customers'
  productId: number | null
  productFeatureId: number | null
}

function CustomerWorldMapPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  
  const [customers, setCustomers] = useState<Customer[]>([])
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]) // Unfiltered customers for counts
  const [loading, setLoading] = useState(true)
  const [productHierarchy, setProductHierarchy] = useState<ProductHierarchy>({
    products: [],
    product_features: {},
  })
  
  const [filters, setFilters] = useState<WorldMapFilters>({
    customerType: 'all',
    productId: null,
    productFeatureId: null,
  })

  // Load product hierarchy for filters
  const loadProductHierarchy = useCallback(async () => {
    try {
      const hierarchy = await productsApi.getProductHierarchy()
      setProductHierarchy({
        products: hierarchy.products,
        product_features: hierarchy.productFeatures,
      })
    } catch (error: any) {
      console.error('Error loading product hierarchy:', error)
      toast({
        title: "Warning",
        description: "Failed to load product filters. Basic filtering will still work.",
        variant: "destructive",
      })
    }
  }, [toast])

  // Load all customers initially (unfiltered)
  const loadAllCustomers = useCallback(async () => {
    try {
      // `sort`/`dir` are the names the API reads (`sort_by`/`sort_direction`
      // were the old, inert pair), and `company` is the customers sort key —
      // there is no `name` column in `sortable_columns`.
      const response = await customersApi.getAll({
        page: 1, 
        per_page: 10000,
        sort: 'company',
        dir: 'asc'
      })
      setAllCustomers(response.customers)
    } catch (error: any) {
      console.error('Error loading all customers:', error)
      toast({
        title: "Error",
        description: "Failed to load customers",
        variant: "destructive",
      })
    }
  }, [toast])

  // Load customers with filters applied
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true)
      
      // Build filter parameters
      const filterParams: any = {
        page: 1, 
        per_page: 10000, // Load all customers
        sort: 'company',
        dir: 'asc'
      }

      // Add customer type filter
      if (filters.customerType === 'leads') {
        filterParams.customer_type = 'Lead'
      } else if (filters.customerType === 'customers') {
        filterParams.customer_type = 'Customer'
      }

      // Add product filters (these will need backend API support)
      if (filters.productId) {
        filterParams.product_id = filters.productId
      }
      if (filters.productFeatureId) {
        filterParams.product_feature_id = filters.productFeatureId
      }
      
      const response = await customersApi.getAll(filterParams)
      setCustomers(response.customers)
      
      if (response.customers.length === 0) {
        toast({
          title: "No Results Found",
          description: "No customers match the selected filters.",
        })
      }
    } catch (error: any) {
      console.error('Error loading customers:', error)
      toast({
        title: "Error",
        description: "Failed to load customers for the map",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [filters, toast])

  // Load product hierarchy and all customers on mount
  useEffect(() => {
    loadProductHierarchy()
    loadAllCustomers()
  }, [loadProductHierarchy, loadAllCustomers])

  // Load filtered customers when filters change
  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  // Filter handling functions
  const handleFilterChange = (filterType: keyof WorldMapFilters, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev, [filterType]: value }

      // Reset dependent filters when parent changes
      if (filterType === 'productId') {
        newFilters.productFeatureId = null
      } else if (filterType === 'productFeatureId') {
      }

      return newFilters
    })
  }

  // Specific handler for removing individual filters
  const handleRemoveFilter = (filterType: keyof WorldMapFilters) => {
    const defaultValue = filterType === 'customerType' ? 'all' : null
    handleFilterChange(filterType, defaultValue)
  }

  const resetFilters = () => {
    setFilters({
      customerType: 'all',
      productId: null,
      productFeatureId: null,
      })
  }

  const getActiveFilterCount = () => {
    let count = 0
    if (filters.customerType !== 'all') count++
    if (filters.productId) count++
    if (filters.productFeatureId) count++
    return count
  }

  // Get available product features for selected product
  const availableFeatures = filters.productId 
    ? productHierarchy.product_features[filters.productId] || []
    : []

  // Handle view customer details
  const handleViewCustomer = (customer: Customer) => {
    navigate(`/crm/customers/${customer.id}`)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* World Map with integrated filters */}
      <CustomerWorldMap
        customers={customers}
        allCustomers={allCustomers}
        loading={loading}
        onCustomerClick={handleViewCustomer}
        hasProductFilter={filters.productId !== null}
        productHierarchy={productHierarchy}
        filters={filters}
        onFilterChange={handleFilterChange}
        onRemoveFilter={handleRemoveFilter}
        onResetFilters={resetFilters}
        getActiveFilterCount={getActiveFilterCount}
      />
    </Box>
  )
}

export default withCustomersAccess(CustomerWorldMapPage)
